/* eslint-disable @next/next/no-img-element */
import styled from '@emotion/styled';
import * as React from 'react';
import { Spacings } from '@styles/index';
import EmptyEducatorState from '@icon/EducatorPhotoRevamp/EmptyEducatorState';
import styleHelper from '@utils/styleHelper';
import Image from '@base/Image';

interface IStyle {
  $color: string;
}

interface INewImage {
  $visibility: string;
}

const CircleBackgroundWrapper = styled.div<IStyle>`
  position: absolute;
  right: -${Spacings.SPACING_3B};
  bottom: ${Spacings.SPACING_3B};
  width: 98px;
  height: 98px;
  background: ${({ $color }) => $color};
  border-radius: 50%;
`;

const OldImageCircleBackgroundWrapper = styled(CircleBackgroundWrapper)`
  overflow: hidden;
`;

const CircleSmallWrapper = styled.div<IStyle>`
  position: absolute;
  right: ${Spacings.SPACING_20B};
  bottom: ${Spacings.SPACING_16B};
  width: ${Spacings.SPACING_3B};
  height: ${Spacings.SPACING_3B};
  background: ${({ $color }) => $color};
  border-radius: 50%;
`;

const CircleLargeWrapper = styled.div<IStyle>`
  position: absolute;
  right: -5px;
  bottom: 86px;
  width: ${Spacings.SPACING_8B};
  height: ${Spacings.SPACING_8B};
  background: ${({ $color }) => $color};
  border-radius: 50%;
`;

const ImageWrapper = styled.div`
  width: 110px;
  height: 138px;
  position: absolute;
  bottom: -${Spacings.SPACING_5B};
  right: 0;
  padding-right: ${Spacings.SPACING_2B};
  padding-top: ${Spacings.SPACING_6B};
`;

const NewEducatorImage = styled.img<INewImage>`
  visibility: ${({ $visibility }) => $visibility};
`;

const NewImage = styled(Image)`
  & {
    height: 138px;
    width: auto;
    max-height: 100%;
    margin: 0 auto;
    object-fit: contain;
  }
`;

const OldImageWrapper = styled.div`
  position: absolute;
  height: 96px;
  width: fit-content;
  bottom: -${Spacings.SPACING_1};
  left: 0;
  right: 0;
  margin: 0 auto;
  padding-top: ${Spacings.SPACING_2B};
`;

const EmptyEducatorWrapper = styled.div`
  position: absolute;
  right: -${Spacings.SPACING_3B};
  bottom: ${Spacings.SPACING_3B};
`;

const FallbackImage = styled(Image)`
  border-radius: 50%;
  width: 98px;
  height: 98px;
  object-fit: cover;
`;

interface IEducatorThumnail {
  isCancelled: boolean;
  color: string;
  fallback: string;
  newThumbnail: string;
  oldThumbnail: string;
}
const EducatorThumbnail: React.FC<IEducatorThumnail> = ({
  color,
  fallback,
  newThumbnail,
  oldThumbnail,
  isCancelled
}) => {
  if (!newThumbnail && !oldThumbnail && !fallback) {
    return (
      <EmptyEducatorWrapper>
        <EmptyEducatorState />
      </EmptyEducatorWrapper>
    );
  }
  const getBgColor = () => {
    if (isCancelled) return 'var(--color-i-gray-light-25)';
    if (color === 'var(--color-i-black)')
      return 'var(--color-i-black-light-25)';
    return styleHelper.hexToRgb(color, 0.3);
  };

  if (newThumbnail) {
    return (
      <>
        <CircleLargeWrapper $color={color} />
        <CircleSmallWrapper $color={color} />
        <CircleBackgroundWrapper $color={getBgColor()} />
        <ImageWrapper>
          <NewImage
            src={`${newThumbnail}?h=276`}
            alt="unacademy"
            layout="fill"
          />
        </ImageWrapper>
      </>
    );
  }

  if (oldThumbnail) {
    return (
      <>
        <CircleLargeWrapper $color={color} />
        <CircleSmallWrapper $color={color} />
        <OldImageCircleBackgroundWrapper $color={getBgColor()}>
          <OldImageWrapper>
            <NewImage
              src={`${oldThumbnail}?h=200`}
              alt="Avatar"
              height={96}
              width={96}
            />
          </OldImageWrapper>
        </OldImageCircleBackgroundWrapper>
      </>
    );
  }

  if (fallback) {
    return (
      <>
        <CircleLargeWrapper $color={color} />
        <CircleSmallWrapper $color={color} />
        <OldImageCircleBackgroundWrapper $color={getBgColor()}>
          <FallbackImage
            src={`${fallback}?h=200`}
            width={98}
            height={98}
            alt=""
          />
        </OldImageCircleBackgroundWrapper>
      </>
    );
  }

  return null;
};

export default EducatorThumbnail;
